import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import LeafgateLogo from "./LeafgateLogo";

const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item?.msg || String(item)).join(" ");
  }

  return "No se pudo verificar el email. El enlace puede ser inválido o haber caducado.";
};

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [status, setStatus] = useState(token ? STATUS.IDLE : STATUS.ERROR);
  const [message, setMessage] = useState(
    token
      ? "Pulsa el botón para verificar tu email. Esto evita que el enlace se consuma por previsualizaciones automáticas del correo."
      : "Falta el token de verificación en el enlace."
  );

  useEffect(() => {
    if (!token) {
      setStatus(STATUS.ERROR);
      setMessage("Falta el token de verificación en el enlace.");
      return;
    }

    setStatus(STATUS.IDLE);
    setMessage(
      "Pulsa el botón para verificar tu email. Esto evita que el enlace se consuma por previsualizaciones automáticas del correo."
    );
  }, [token]);

  const handleVerify = async () => {
    if (!token || status === STATUS.LOADING) {
      return;
    }

    setStatus(STATUS.LOADING);
    setMessage("Verificando tu email...");

    try {
      await axios.post("/v1/users/verify-email", null, {
        params: { token },
      });
      setStatus(STATUS.SUCCESS);
      setMessage("Email verificado correctamente. Ya puedes iniciar sesión.");
    } catch (error) {
      setStatus(STATUS.ERROR);
      setMessage(getErrorMessage(error));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <LeafgateLogo size={28} color="#191A1F" />
          <span className="text-xl font-bold text-gray-900">ClubsAgent</span>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-3">Verificación de email</h1>

        <p
          className={`text-sm leading-6 mb-6 ${
            status === STATUS.SUCCESS
              ? "text-green-700"
              : status === STATUS.ERROR
              ? "text-red-600"
              : "text-gray-700"
          }`}
        >
          {message}
        </p>

        {status === STATUS.LOADING && (
          <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden mb-6">
            <div className="h-full w-1/2 bg-violet-500 animate-pulse" />
          </div>
        )}

        {status !== STATUS.SUCCESS && token && (
          <button
            type="button"
            onClick={handleVerify}
            disabled={status === STATUS.LOADING}
            className="inline-flex w-full justify-center rounded-xl bg-violet-600 px-4 py-3 text-white font-semibold hover:bg-violet-700 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
          >
            {status === STATUS.LOADING ? "Verificando..." : "Verificar mi email"}
          </button>
        )}

        {(status === STATUS.SUCCESS || status === STATUS.ERROR || !token) && (
          <Link
            to="/login"
            className="inline-flex w-full justify-center rounded-xl bg-violet-600 px-4 py-3 text-white font-semibold hover:bg-violet-700 transition-colors"
          >
            Ir a iniciar sesión
          </Link>
        )}
      </div>
    </div>
  );
}
